import { useState, FunctionComponent, useEffect } from 'react';
import { asyncWithLDProvider, useFlags as LDUseFlags } from 'launchdarkly-react-client-sdk';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import { LD_CLIENT_ID, LD_RELAY_SERVICE_URL } from './public-config';
import { setCookiesWithDomain } from '../utils/cookies';

const LD_ANONYMOUS_ID_COOKIE = 'ld_anonymous_id';

// LD docs: https://docs.launchdarkly.com/sdk/client-side/react/react-web#asyncwithldprovider
export const asyncLDProvider = (user: any): Promise<any> => {
  const ldAnonymousId = Cookies.get(LD_ANONYMOUS_ID_COOKIE);

  // make sure we can get ldAnonymousId from browser cookies
  if (!ldAnonymousId) {
    const anonymousUserId = uuidv4();
    setCookiesWithDomain(LD_ANONYMOUS_ID_COOKIE, anonymousUserId);
  }

  const ldUserId = Cookies.get(LD_ANONYMOUS_ID_COOKIE);

  const ldUserTraits: any = user
    ? {
        userId: user.user_id,
        isOrganizationAdmin: user.is_organization_admin,
        userType: user.user_type,
        creationTime: user.creation_time,
        organizationId: user.organization?.id,
        isSaucelabsUser: (user.email?.toLowerCase().endsWith('@saucelabs.com') && user.verified) ?? false,
        ancestorUserType: user.ancestor_user_type,
      }
    : {};

  return asyncWithLDProvider({
    clientSideID: LD_CLIENT_ID,
    context: {
      kind: 'user',
      key: user?.user_id || ldUserId, // we need this to uniquely identify a user
      ...ldUserTraits,
      // add more user info here as is needed
    },
    options: {
      bootstrap: 'localStorage',
      baseUrl: LD_RELAY_SERVICE_URL,
      streamUrl: LD_RELAY_SERVICE_URL,
      eventsUrl: LD_RELAY_SERVICE_URL,
      sendEvents: true,
      sendLDHeaders: false,
      streaming: false,
    },
  });
};

let LDProvider: FunctionComponent | undefined;

// this code is necessary as Next.js _app does not allow async functions
// altogether it prevents a flash of default flags on startup
export function useLDProvider(user: any): FunctionComponent<{ children: React.ReactNode }> {
  const [isLoaded, setLoaded] = useState(false);
  const Loading = () => <div />;
  useEffect((): void => {
    (async () => {
      LDProvider = await asyncLDProvider(user);
      setLoaded(true);
    })();
  }, [user]);
  if (!isLoaded || !LDProvider) {
    return Loading;
  }
  return LDProvider;
}

// example usage: https://docs.launchdarkly.com/sdk/client-side/react#hooks
export const useFlags = LDUseFlags;
